// ── ASSEMBLING THE COVERAGE-LEVEL REPLAY FOR ONE FARM ────────────────────────
//
// The replay itself is levelReplay.js and knows nothing about tables.  This is the
// loader: it fetches the farm's APH databases and RMA's published prices, and hands
// them over in the shape the replay takes.
//
// ⚠ IT IS ONLY EVER CALLED WHEN THE OPTIMIZER IS SWITCHED ON.  Two reads (one per
// farm, one price table) is a real cost on a card that already does a dozen, and the
// optimizer defaults OFF — so nothing here runs for the ordinary quote.
import { aphAsOf, replayDatabase, rollUp, REPLAY_REFUSED, REFUSAL_TEXT } from "./levelReplay.js";
import { allLevels } from "./levelLadder.js";

export const WHEAT_COMMODITY = "0011";
export const RP_PLAN = "02";
// The five states the wheat quoter rates.  A farm outside them has no rate table and
// is reported as such rather than quoted against a neighbour's.
export const STATE_FIPS = { NY: "36", PA: "42", OH: "39", MD: "24", IN: "18" };

// A database is one APH database, and the CY2026 and CY2027 copies of it are the SAME
// database — `policy_number` carries the crop-year suffix, so it is stripped.  A yield
// year restated by a later policy file wins, because that is the AIP's newest statement
// of it.
export const policyStem = (p) => String(p || "").replace(/-\d{2}$/, "");

export function groupDatabases(rows, { county = null, practice = null } = {}) {
  const out = {};
  for (const r of rows || []) {
    if (county && String(r.county || "").toUpperCase() !== String(county).toUpperCase()) continue;
    if (practice && String(r.practice || "") !== String(practice)) continue;
    const key = [policyStem(r.policy_number), r.yield_key || "", r.unit_number || "", r.sub_unit || ""].join("|");
    const d = (out[key] ||= { key, county: r.county, state: r.state, practice: r.practice,
                              typeCode: r.type_code, unit: r.unit_number, sub: r.sub_unit, years: {} });
    const prev = d.years[r.crop_year];
    if (!prev || String(r.policy_number) > String(prev._pol))
      d.years[r.crop_year] = { cropYear: r.crop_year, yield: r.yield_per_acre, acres: r.acres,
                               recordType: r.record_type, _pol: r.policy_number };
  }
  return Object.values(out);
}

// prices: stateFips|practice -> year -> { projected, harvest }.
//
// ⚠ KEYED ON PRACTICE, NOT JUST STATE.  Organic wheat carries its own election — NY
// CY2026 is $5.76 conventional against $11.16 organic — so a state-only key would put
// a conventional price on an organic database and understate its guarantee by half.
export function indexPrices(rows) {
  const by = {}; const disagree = [];
  for (const r of rows || []) {
    const k = `${r.state_code}|${r.practice_code}`;
    const y = ((by[k] ||= {})[r.crop_year] ||= { projected: null, harvest: null });
    for (const f of ["projected", "harvest"]) {
      const v = r[`${f}_price`] == null ? null : +r[`${f}_price`];
      if (v == null) continue;
      if (y[f] == null) y[f] = v;
      else if (y[f] !== v) disagree.push(`${k} ${r.crop_year} ${f}`);
    }
  }
  return { by, disagree };
}

// `quoteRP` is the SHIPPED quoteWheatRP2027, injected — this module never imports the
// 2.5 MB rate table, and the checker can drive it with a stub.
//
// ⚠ NOTHING DEFAULTS THE ACREAGE.  Each replay year is priced on the AIP's OWN reported
// acres for that database; a year without them gets NO premium rather than an invented
// one, because acres move the BU/EU discount band and a hardcoded figure is a premium
// nobody was charged.
export function makePremiumFn({ quoteRP, stateFips, county, practice, unitStructure, quotePrice, years, vol = 0.20 }) {
  if (typeof quoteRP !== "function" || !(quotePrice > 0)) return () => null;
  return (level, year, aph) => {
    const row = years && years[year];
    const ac = row && row.acres != null ? +row.acres : null;
    if (!(ac > 0) || !(aph > 0)) return null;
    const q = quoteRP({ state: stateFips, county, aph, cl: level, acres: ac,
                        unit: unitStructure || "BU", price: quotePrice, vol, plan: "RP", practice });
    if (!q) return null;
    return q.netPerAcre != null ? q.netPerAcre : (q.netTotal != null ? q.netTotal / ac : null);
  };
}

// ── THE WHOLE THING ──────────────────────────────────────────────────────────
//
// Returns { years, totals, best, refusals, databases, priceBasis } or { error }.
export function buildLevelReplay({ dbRows, priceRows, county, practice, state, unitStructure,
                                   quoteRP, quotePrice, levels = null }) {
  const LV = levels || allLevels();
  const dbs = groupDatabases(dbRows, { county, practice });
  if (!dbs.length) return { error: "no wheat APH databases on file for this county and practice" };

  const { by: priceBy, disagree } = indexPrices(priceRows);
  const stFips = STATE_FIPS[String(state || "").toUpperCase()] || String(state || "");
  const pr = /^\d+$/.test(String(practice || "")) ? String(practice) : "003";
  const prices = priceBy[`${stFips}|${pr}`] || {};
  if (!Object.keys(prices).length)
    return { error: "RMA's published wheat prices for this state and practice are not in our data" };

  const per = dbs.map((d) => replayDatabase({
    years: d.years, prices, levels: LV,
    premiumFor: makePremiumFn({ quoteRP, stateFips: stFips, county, practice: pr,
                                unitStructure, quotePrice, years: d.years }),
  }));
  const rolled = rollUp(per, LV);

  const refusals = {};
  for (const p of per) for (const [k, v] of Object.entries(p.refusals || {})) refusals[k] = (refusals[k] || 0) + v;

  // ⚠ "BEST ON THIS RECORD" IS NOT A RECOMMENDATION AND THE FIELD IS NAMED THAT WAY.
  // Over a short, benign record the cheapest level almost always nets best — measured
  // book-wide, 54 of 105 replayable wheat farms land on the BOTTOM of the ladder —
  // because insurance costs money in the years it is not needed. The ledger is the
  // product; this field exists so a reader does not have to scan the column, and it
  // carries its own sample size so it cannot be read as a verdict.
  const priced = LV.map((L) => rolled.totals[L.toFixed(2)]).filter((t) => t && t.priced && t.years > 0);
  const best = priced.length ? priced.reduce((a, b) => (b.netPerAc > a.netPerAc ? b : a)) : null;

  return {
    years: rolled.years, totals: rolled.totals, best,
    levels: LV, databases: dbs.length, refusals,
    yearsCovered: rolled.years.length,
    lossYears: priced.length ? Math.max(...priced.map((t) => t.lossYears)) : 0,
    aphExact: rolled.years.length > 0 && rolled.years.every((y) => y.aphExact),
    aphMinYears: rolled.years.length ? Math.min(...rolled.years.map((y) => y.aphMinYears || 0)) : null,
    priceDisagreements: disagree,
    error: null,
  };
}

export { REPLAY_REFUSED, REFUSAL_TEXT, aphAsOf };
